import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useVisibleBannersStore } from '@/stores/visibleBannersStore';
import { SystemBanner } from '@/components/domain/shared/SystemBanner';
import { useSystemBanners } from '@/hooks/useSystemBanners';
import { useDismissBanner } from '@/hooks/useDismissBanner';
import type {
  BannerScope,
  BannerPageContext,
  BannerConfig,
} from '@/config/banners';

export interface SystemBannerSlotProps {
  scope: BannerScope;
  pageContext?: BannerPageContext;
  householdId: string | null | undefined;
}

// Routes where banners are intentionally hidden. The onboarding flow
// owns its own header treatment and shouldn't be cluttered with system
// banners that prompt users back into the app shell mid-setup.
const SUPPRESSED_PATH_PREFIXES = ['/onboarding'];

export function SystemBannerSlot({
  scope,
  pageContext,
  householdId,
}: SystemBannerSlotProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { banners } = useSystemBanners({ scope, pageContext, householdId });
  const { dismiss } = useDismissBanner();

  const suppressed = SUPPRESSED_PATH_PREFIXES.some((prefix) =>
    location.pathname.startsWith(prefix),
  );

  // Publish what the GLOBAL slot shows, so the email status toast can stay
  // away while a Gmail banner is up (YAR-477).
  const shownTypes =
    scope === 'global' && !suppressed && banners.length > 0
      ? banners[0].type
      : null;
  useEffect(() => {
    if (scope !== 'global') return;
    useVisibleBannersStore
      .getState()
      .setGlobalTypes(shownTypes ? [shownTypes] : []);
  }, [scope, shownTypes]);
  useEffect(() => {
    if (scope !== 'global') return;
    return () => useVisibleBannersStore.getState().setGlobalTypes([]);
  }, [scope]);

  if (suppressed) return null;
  if (banners.length === 0) return null;

  const visible: BannerConfig[] = scope === 'global' ? [banners[0]] : banners;

  return (
    <div className="flex flex-col gap-2">
      {visible.map((banner) => {
        // Hide the CTA when its target is the page the user is already on —
        // otherwise the button reads "Connect Gmail" but `navigate()` to the
        // current pathname is a no-op, making the banner look broken
        // (e.g. on /settings/forwarding the page already owns the connect flow).
        const onAction =
          banner.action && banner.action.route !== location.pathname
            ? () => navigate(banner.action!.route)
            : undefined;
        return (
          <SystemBanner
            key={banner.type}
            banner={banner}
            onAction={onAction}
            onDismiss={
              banner.dismiss === 'none'
                ? undefined
                : () =>
                    void dismiss({
                      type: banner.type,
                      dismiss: banner.dismiss,
                      defaultSnoozeDays: banner.defaultSnoozeDays,
                    })
            }
          />
        );
      })}
    </div>
  );
}
